import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/recording.py RecordingInfo.
export interface RecordingInfo {
  filename: string;
  size_bytes: number;
  extension: string;
  /** Length of the audio. null when the file could not be read — the server
   *  returns null rather than a guess, because a wrong duration on a prompt
   *  someone is about to attach to a campaign is worse than none. */
  duration_seconds: number | null;
  nickname: string | null;
  // Canonical value stored in campaign_detail.recording_file (e.g.
  // "ictbroadcast/<sanitized>") — the dialer-playable reference. Use this as the
  // <option value> in campaign forms so a recording selection round-trips: the
  // form shows the current pick and saves a value the dialer can actually play.
  ref: string;
}

export async function listRecordings(): Promise<RecordingInfo[]> {
  // Route is at "/" (trailing slash avoids 307).
  const resp = await api.get<APIResponse<RecordingInfo[]>>("/recordings/");
  return unwrap(resp.data);
}

export interface UploadedRecording {
  filename: string;
  size_bytes: number;
  duration_sec: number | null;
  // Playable reference to assign to a campaign. Present because the upload
  // converts and publishes the audio before returning — a successful upload is
  // already usable, with no waiting.
  ref: string;
}

export async function uploadRecording(
  file: File,
  nickname?: string,
): Promise<UploadedRecording> {
  const fd = new FormData();
  fd.append("file", file);
  if (nickname && nickname.trim()) fd.append("nickname", nickname.trim());
  const resp = await api.post<APIResponse<UploadedRecording>>(
    "/recordings/upload",
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return unwrap(resp.data);
}

export async function setRecordingNickname(filename: string, nickname: string): Promise<void> {
  await api.put(`/recordings/${encodeURIComponent(filename)}/nickname`, { nickname });
}

export async function fetchRecordingAudioUrl(filename: string): Promise<string> {
  // Fetch with the auth header (an <audio src> can't send it), then play the blob.
  const resp = await api.get(`/recordings/${encodeURIComponent(filename)}/audio`, {
    responseType: "blob",
  });
  return URL.createObjectURL(resp.data as Blob);
}

export async function deleteRecording(filename: string): Promise<void> {
  await api.delete(`/recordings/${encodeURIComponent(filename)}`);
}
